import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { driveClient } from '@/lib/drive/driveClient';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { formatBytes } from '@/lib/drive/format';

/**
 * The storage meter: used against quota, read from the drive's own profile
 * (`quotaOf`), refreshed after uploads and on the live `storageWarning` event.
 * A number here is a number the ledger stands behind — never a hardcoded zero.
 */
export function StorageMeter() {
  const { t } = useTranslation();
  const { socket } = useWebSocket();
  const [used, setUsed] = useState<number | null>(null);
  const [hard, setHard] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const profile = await driveClient.fetchProfile();
      setUsed(Number(profile.user.storageUsed));
      setHard(Number(profile.user.storageLimit));
    } catch {
      /* anonymous or unreachable: the meter just stays blank */
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!socket) return;
    const onChange = () => void refresh();
    socket.on('storageWarning', onChange);
    socket.on('fileUploaded', onChange);
    socket.on('filePurged', onChange);
    return () => {
      socket.off('storageWarning', onChange);
      socket.off('fileUploaded', onChange);
      socket.off('filePurged', onChange);
    };
  }, [socket, refresh]);

  if (used === null || hard === null || hard <= 0) {
    return (
      <div className="min-h-16 rounded-lg border border-border-color bg-card p-3" aria-hidden="true">
        <div className="h-2 w-full rounded-full bg-muted" />
      </div>
    );
  }

  const percent = Math.min(100, Math.round((used / hard) * 100));
  const tone = percent >= 90 ? 'bg-destructive' : percent >= 75 ? 'bg-warning' : 'bg-accent';

  return (
    <div className="rounded-lg border border-border-color bg-card p-3" data-tour-id="storage-meter">
      <div className="flex items-center justify-between gap-2">
        <p className="whitespace-nowrap text-xs font-medium text-muted-foreground">{t('drive.storage')}</p>
        <p className="whitespace-nowrap text-xs text-muted-foreground">{percent}%</p>
      </div>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${percent}%` }} />
      </div>
      <p className="mt-2 whitespace-nowrap text-xs text-muted-foreground">
        {formatBytes(used)} {t('drive.storageOf')} {formatBytes(hard)}
      </p>
    </div>
  );
}
